import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

type VariantAvailabilityPickerProps = {
  // true = mark as unavailable, false = mark as available again.
  onChange: (unavailable: boolean) => void;
  disabled?: boolean;
  className?: string;
};

// Bulk-toolbar dropdown that sits next to AnnouncementPicker. Always shows
// its placeholder - there's no single "current" availability across a selection.
export function VariantAvailabilityPicker({
  onChange,
  disabled,
  className,
}: VariantAvailabilityPickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const select = (unavailable: boolean) => {
    onChange(unavailable);
    setIsOpen(false);
  };

  const options = [
    { unavailable: true, label: t("products.markUnavailable") },
    { unavailable: false, label: t("products.markAvailable") },
  ];

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(prev => !prev)}
        className={`${className ?? ""} flex items-center gap-2 text-left disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer`}
      >
        <span className="flex-1 min-w-0 truncate text-app-muted">{t("products.setAvailability")}</span>
        <ChevronDown size={16} className={`shrink-0 text-app-muted transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <div
        className={`absolute z-50 top-full mt-1 left-0 w-full min-w-56 bg-app-surface text-foreground rounded-xl shadow-lg ring-1 ring-app-border overflow-hidden transition-all duration-150 ease-in-out ${
          isOpen ? "opacity-100 translate-y-0 scale-100" : "opacity-0 -translate-y-1 scale-95 pointer-events-none"
        }`}
      >
        <ul className="py-1">
          {options.map(option => (
            <li key={String(option.unavailable)}>
              <button
                type="button"
                onClick={() => select(option.unavailable)}
                className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-app-surface-hover transition-colors duration-150 cursor-pointer"
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
